import React, { useState, useEffect } from 'react';
import { 
  Trophy, 
  Award, 
  Star, 
  Clock, 
  BookOpen, 
  Zap, 
  Users, 
  Target, 
  AlertCircle // Added this import
} from 'lucide-react';

// Adjust the import path to correctly point to your firebase.js
import { 
  auth, 
  db, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  onSnapshot, 
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  onAuthStateChanged
} from '../../firebase'; // Note the adjusted path

// Badge definitions - keeping this outside component for reference
const badgeDefinitions = [
  { id: 1, name: "Early Bird", description: "Complete 5 study sessions before 9 AM", icon: <Clock size={24} />, requirementType: "earlyMorningCount", requirementValue: 5 },
  { id: 2, name: "Bookworm", description: "Study for 20 hours total", icon: <BookOpen size={24} />, requirementType: "totalHours", requirementValue: 20 },
  { id: 3, name: "Focus Master", description: "Complete a 2-hour session without breaks", icon: <Target size={24} />, requirementType: "longSessionWithoutBreak", requirementValue: 1 },
  { id: 4, name: "Social Learner", description: "Study with 3 friends", icon: <Users size={24} />, requirementType: "uniqueStudyPartners", requirementValue: 3 },
  { id: 5, name: "Speed Demon", description: "Complete 10 quick quizzes", icon: <Zap size={24} />, requirementType: "quickQuizzesCompleted", requirementValue: 10 },
  { id: 6, name: "Weekend Warrior", description: "Study for 5 hours on a weekend", icon: <Star size={24} />, requirementType: "weekendStudyHours", requirementValue: 5 },
];

// Helper functions for XP and level calculations
const calculateXpForNextLevel = (level) => {
  // Example: Each level requires 25% more XP than the previous
  return Math.round(1000 * Math.pow(1.25, level - 1));
};

const RewardsPage = () => {
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Safely calculate XP percentage
  const xpPercentage = userData 
    ? (userData.xp / userData.xpToNextLevel) * 100 
    : 0;

  useEffect(() => {
    // Comprehensive authentication and data fetching
    const unsubscribe = onAuthStateChanged(
      auth, 
      async (user) => {
        if (user) {
          try {
            const userDocRef = doc(db, 'userRewards', user.uid);
            
            // First, try to get the document
            const userDoc = await getDoc(userDocRef);

            // If document doesn't exist, create initial document
            if (!userDoc.exists()) {
              const initialData = {
                name: user.displayName || '',
                level: 1,
                xp: 0,
                xpToNextLevel: 1000,
                streak: 0,
                studyStats: {
                  totalHours: 0,
                  sessionsCompleted: 0,
                  earlyMorningCount: 0,
                  longSessionWithoutBreak: 0,
                  uniqueStudyPartners: 0,
                  quickQuizzesCompleted: 0,
                  weekendStudyHours: 0
                },
                badges: badgeDefinitions.map(badge => ({
                  id: badge.id,
                  earned: false
                })),
                lastUpdated: serverTimestamp(),
                createdAt: serverTimestamp()
              };

              await setDoc(userDocRef, initialData);
            }

            // Set up real-time listener
            const unsubscribeSnapshot = onSnapshot(
              userDocRef,
              (doc) => {
                if (doc.exists()) {
                  const data = doc.data();
                  
                  // Merge badge definitions with user badge progress
                  const mergedBadges = badgeDefinitions.map(badgeDef => {
                    const userBadge = data.badges?.find(b => b.id === badgeDef.id);
                    return {
                      ...badgeDef,
                      earned: userBadge?.earned || false
                    };
                  });

                  setUserData({
                    ...data,
                    badges: mergedBadges
                  });
                }
                setLoading(false);
              },
              (snapshotError) => {
                console.error('Snapshot error:', snapshotError);
                setError(snapshotError.message);
                setLoading(false);
              }
            );

            setUserId(user.uid);

            // Return cleanup function
            return () => {
              unsubscribeSnapshot();
            };

          } catch (err) {
            console.error('Error setting up rewards tracking:', err);
            setError(err.message);
            setLoading(false);
          }
        } else {
          // No user signed in
          setUserId(null);
          setUserData(null);
          setLoading(false);
        }
      },
      (authError) => {
        console.error('Auth state change error:', authError);
        setError(authError.message);
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Optional: Function to simulate or manually track a study session
  const simulateStudySession = async () => {
    if (!userId) {
      console.error('No user logged in');
      return;
    }

    try {
      const userDocRef = doc(db, 'userRewards', userId);
      
      // Simulate a study session
      const sessionData = {
        durationMinutes: 45,
        completedAt: new Date().toISOString(),
        hadBreaks: false,
        quizCompleted: true,
        quizDurationSeconds: 150,
        studyPartners: ["partner1", "partner2"]
      };

      // Call the tracking function (this would typically be in a separate service)
      const trackStudySession = async (sessionData) => {
        const userDoc = await getDoc(userDocRef);
        if (!userDoc.exists()) return;

        const userData = userDoc.data();
        let updatedStats = { ...userData.studyStats };
        let earnedXp = sessionData.durationMinutes * 10; // 10 XP per minute

        // Update relevant stats (simplified version of the original implementation)
        updatedStats.totalHours += sessionData.durationMinutes / 60;
        updatedStats.sessionsCompleted += 1;

        // Calculate new XP and potential level up
        let newXp = userData.xp + earnedXp;
        let newLevel = userData.level;
        let newXpToNextLevel = userData.xpToNextLevel;

        while (newXp >= newXpToNextLevel) {
          newXp -= newXpToNextLevel;
          newLevel++;
          newXpToNextLevel = calculateXpForNextLevel(newLevel);
        }

        // Update the document
        await updateDoc(userDocRef, {
          xp: newXp,
          level: newLevel,
          xpToNextLevel: newXpToNextLevel,
          studyStats: updatedStats,
          lastUpdated: serverTimestamp()
        });
      };

      await trackStudySession(sessionData);
    } catch (error) {
      console.error('Error simulating study session:', error);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center">
        <div className="text-center">
          <Trophy className="mx-auto mb-4 text-blue-500" size={48} />
          <p className="text-gray-600">Loading rewards data...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="p-6 text-center bg-red-50">
        <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
        <p className="text-red-600">Error: {error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 bg-red-500 text-white px-4 py-2 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  // Render main content when user data is available
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Rewards</h1>
      
      {/* Level and XP Section */}
      <div className="bg-blue-50 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-blue-800">
              Level {userData.level}
            </h2>
            <p className="text-blue-600">
              {userData.xp} / {userData.xpToNextLevel} XP to next level
            </p>
          </div>
          <div className="bg-blue-600 text-white rounded-full p-4">
            <Trophy size={32} />
          </div>
        </div>
        
        {/* XP Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div 
            className="bg-blue-600 h-4 rounded-full transition-all duration-500" 
            style={{ width: `${xpPercentage}%` }}
          ></div>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg text-center">
          <h3 className="font-bold text-green-800 mb-1">Study Streak</h3>
          <div className="flex items-center justify-center gap-2">
            <Zap size={20} className="text-green-600" />
            <span className="text-2xl font-bold text-green-600">
              {userData.streak || 0} days
            </span>
          </div>
        </div>
        
        <div className="bg-amber-50 p-4 rounded-lg text-center">
          <h3 className="font-bold text-amber-800 mb-1">Badges Earned</h3>
          <div className="flex items-center justify-center gap-2">
            <Award size={20} className="text-amber-600" />
            <span className="text-2xl font-bold text-amber-600">
              {userData.badges.filter(badge => badge.earned).length} / {userData.badges.length}
            </span>
          </div>
        </div>
      </div>
      
      {/* Badges Section */}
      <h2 className="text-xl font-bold mb-4">Badges</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {userData.badges.map((badge) => (
          <div 
            key={badge.id} 
            className={`border rounded-lg p-4 flex items-center ${
              badge.earned ? 'bg-white border-green-300' : 'bg-gray-50 border-gray-200 opacity-70'
            }`}
          >
            <div className={`p-3 rounded-full mr-3 ${
              badge.earned ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-500'
            }`}>
              {badge.icon}
            </div>
            <div>
              <h3 className="font-bold">{badge.name}</h3>
              <p className="text-sm text-gray-600">{badge.description}</p>
              <p className="text-xs mt-1 font-medium">
                {badge.earned ? 
                  <span className="text-green-600">Earned!</span> : 
                  <span className="text-gray-500">Not yet earned</span>
                }
              </p>
            </div>
          </div>
        ))}
      </div>
      
      {/* How to Earn XP Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">How to Earn XP</h2>
        <ul className="space-y-2">
          <li className="flex items-center gap-2">
            <div className="bg-blue-100 p-1 rounded-full">
              <Clock size={16} className="text-blue-600" />
            </div>
            <span>10 XP per study minute</span>
          </li>
          <li className="flex items-center gap-2">
            <div className="bg-blue-100 p-1 rounded-full">
              <Target size={16} className="text-blue-600" />
            </div>
            <span>50 XP for completing daily goal</span>
          </li>
          <li className="flex items-center gap-2">
            <div className="bg-blue-100 p-1 rounded-full">
              <Zap size={16} className="text-blue-600" />
            </div>
            <span>100 XP for 3-day study streak</span>
          </li>
          <li className="flex items-center gap-2">
            <div className="bg-blue-100 p-1 rounded-full">
              <Users size={16} className="text-blue-600" />
            </div>
            <span>200 XP for study group sessions</span>
          </li>
          <li className="flex items-center gap-2">
            <div className="bg-blue-100 p-1 rounded-full">
              <Award size={16} className="text-blue-600" />
            </div>
            <span>500 XP for earning a new badge</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default RewardsPage;